import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
	DIALOG_COLORS,
	DIALOG_FONT_FAMILY,
	dialogFooterSx,
	dialogHeaderSx,
	dialogPaperSx,
	footerButtonSx,
} from "../../../components/checkout-comment-dialog/styles";

export interface MissingProfilingQuestion {
	text: string;
	section: string;
}

const PREVIEW_COUNT = 5;
const TITLE_ID = "missing-profiling-title";

// Question texts can end with an internal tag like "[HIGH SIGNAL]" that means nothing to a salesperson
const cleanQuestionText = (text: string) => text.replace(/\s*\[[A-Z0-9 _-]+\]\s*$/, "").trim();

interface MissingProfilingQuestionsDialogProps {
	// null keeps the dialog closed
	questions: MissingProfilingQuestion[] | null;
	onClose: () => void;
	onComplete: () => void;
}

const MissingProfilingQuestionsDialog = ({ questions, onClose, onComplete }: MissingProfilingQuestionsDialogProps) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [expanded, setExpanded] = useState(false);

	const missing = questions || [];
	useEffect(() => {
		if (questions) setExpanded(false);
	}, [questions]);

	const shown = expanded ? missing : missing.slice(0, PREVIEW_COUNT);
	const extra = missing.length - PREVIEW_COUNT;
	const grouped = shown.reduce((acc: Record<string, string[]>, q) => {
		(acc[q.section] = acc[q.section] || []).push(cleanQuestionText(q.text));
		return acc;
	}, {});

	return (
		<Dialog
			open={questions !== null}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			fullScreen={isSmallScreen}
			aria-labelledby={TITLE_ID}
			sx={dialogPaperSx}
		>
			<DialogTitle component="div" sx={dialogHeaderSx}>
				<Typography id={TITLE_ID} component="h2" sx={{ fontFamily: DIALOG_FONT_FAMILY, fontSize: 22, fontWeight: 700 }}>
					Customer Profiling is incomplete
				</Typography>
				<IconButton
					aria-label="Close"
					onClick={onClose}
					sx={{ position: "absolute", right: 16, top: 20, color: DIALOG_COLORS.muted }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ px: 3.5, pt: 3, pb: 3 }}>
				<Typography sx={{ fontFamily: DIALOG_FONT_FAMILY, fontSize: 15, mt: 1, mb: 2 }}>
					{`Answer the ${missing.length} mandatory question${missing.length === 1 ? "" : "s"} below before converting to a Booking Proposal.`}
				</Typography>
				{Object.entries(grouped).map(([section, texts]) => (
					<Box key={section} sx={{ mb: 2 }}>
						<Typography sx={{ fontFamily: DIALOG_FONT_FAMILY, fontSize: 14, fontWeight: 700, color: DIALOG_COLORS.noteText }}>
							{section}
						</Typography>
						<Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, fontFamily: DIALOG_FONT_FAMILY, fontSize: 14 }}>
							{texts.map((text) => (
								<li key={text} style={{ marginBottom: 2 }}>
									{text}
								</li>
							))}
						</Box>
					</Box>
				))}
				{extra > 0 && (
					<Button
						variant="text"
						aria-expanded={expanded}
						onClick={() => setExpanded((prev) => !prev)}
						sx={{ fontFamily: DIALOG_FONT_FAMILY, textTransform: "none", color: DIALOG_COLORS.link, p: 0, minWidth: 0 }}
					>
						{expanded ? "Show less" : `+ ${extra} more`}
					</Button>
				)}
			</DialogContent>

			<DialogActions sx={dialogFooterSx}>
				<Button
					onClick={onClose}
					variant="outlined"
					sx={{ ...footerButtonSx, color: DIALOG_COLORS.text, borderColor: DIALOG_COLORS.border }}
				>
					Close
				</Button>
				<Button
					onClick={onComplete}
					variant="contained"
					disableElevation
					sx={{
						...footerButtonSx,
						backgroundColor: "#fdd835",
						color: "#054586",
						"&:hover": { backgroundColor: "#fbc02d" },
					}}
				>
					Complete Customer Profiling
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default MissingProfilingQuestionsDialog;
